import { MONTH_NAMES, goalTypeLabel, SEVERITY_LABELS } from './clinicalUtils';
import {
  COMPANY_BRAND, getLogoBase64, genLabel, captureChartImage,
  BRAND_NAVY_RGB, SEMAFORO_RGB, SEVERITY_RGB,
  BRAND_NAVY_ARGB, BRAND_NAVY_LIGHT_ARGB, SEMAFORO_ARGB,
  drawPdfHeader, drawPdfFooter, drawSectionBarPdf, drawKpiCardsPdf, addChartImagePdf, ensurePdfSpace,
  xlsxSectionHeader, xlsxKpiCards, addChartImageXlsx,
} from './brandAssets';

/**
 * clinicalExportUtils — exports gerados no FRONTEND (padrão de qualidade de
 * `components/exportUtils.js`, usado pelos relatórios de Ponto): logo,
 * cabeçalho com identidade visual, tabelas (jspdf-autotable / exceljs) em vez
 * de texto solto, cores condicionais e rodapé paginado.
 * Fonte: payload de GET /clinical-results/dashboard.
 * NÃO usar ISO-8859-1 / largura fixa aqui — isso é exclusivo do AFD/AEJ.
 */

const brl = (v) => Number(v || 0);
const fmtBRL = (v) => `R$ ${brl(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const pct = (v) => (v === null || v === undefined ? '' : `${(Number(v) * 100).toFixed(1)}%`);
const pctOrDash = (v) => (v === null || v === undefined ? '—' : pct(v));
const MONEY_FMT = '"R$" #,##0.00';
const PCT_FMT = '0.0%';

function statusColor(status) {
  return status && status.color ? status.color : null;
}

// ─────────────────────────────────────────────────────────────────────────
// PDF (jsPDF + jspdf-autotable) — tabelas de verdade em vez de doc.text() solto
// ─────────────────────────────────────────────────────────────────────────

export async function exportMonthlyPdf(dashboard, goalTypeCatalog = [], charts = {}) {
  const { company, period, monthly, weeks, counters, alerts } = dashboard;
  const monthLabel = `${MONTH_NAMES[period.month - 1]}/${period.year}`;

  const [{ default: jsPDF }, { default: autoTable }, weeklyChart, weekdayChart] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    captureChartImage(charts.weeklyTrendEl),
    captureChartImage(charts.weekdayEl),
  ]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const logoB64 = await getLogoBase64();

  // ── Cabeçalho ──
  let cursorY = drawPdfHeader(doc, {
    logoB64,
    subtitle: `Resultados da Unidade — ${company.name}`,
    meta: `Período: ${monthLabel}`,
    pageW,
  });

  const margin = { left: 10, right: 10 };
  const baseHeadStyles = { fillColor: BRAND_NAVY_RGB, textColor: [255, 255, 255], fontStyle: 'bold' };
  const baseStyles = { fontSize: 8.5, cellPadding: 3, textColor: [50, 50, 50] };
  const alternateRowStyles = { fillColor: [248, 249, 251] };
  const contentWidth = pageW - margin.left - margin.right;

  // ── Resumo do mês — cards de KPI ──
  cursorY = drawSectionBarPdf(doc, { y: cursorY, width: contentWidth, text: 'RESUMO DO MÊS' });
  const overallColor = SEMAFORO_RGB[monthly.status.overall] || null;
  cursorY = drawKpiCardsPdf(doc, {
    startY: cursorY,
    pageW,
    perRow: 3,
    cards: [
      { label: 'Receita total', value: fmtBRL(monthly.total_revenue) },
      { label: 'Despesas', value: fmtBRL(monthly.total_expenses) },
      { label: 'Lucro líquido', value: fmtBRL(monthly.net_profit) },
      { label: 'Margem líquida', value: pct(monthly.net_margin) },
      { label: 'Conversão em valor', value: pct(monthly.value_conversion) },
      { label: 'Ticket médio', value: fmtBRL(monthly.average_ticket) },
      { label: 'Ticket médio dos orçamentos', value: fmtBRL(monthly.avg_budget_ticket) },
      { label: 'Ticket médio dos orç. fechados', value: fmtBRL(monthly.avg_closed_budget_ticket) },
      { label: 'Atingimento da meta', value: pctOrDash(monthly.achievement) },
      { label: 'Semáforo geral', value: (monthly.status.overall || '—').toUpperCase(), color: overallColor },
    ],
  });

  // ── Gráfico: Tendência das semanas do mês ──
  if (weeklyChart) {
    const imgH = contentWidth * (weeklyChart.height / weeklyChart.width);
    cursorY = ensurePdfSpace(doc, cursorY, 7 + 4 + imgH + 8);
    cursorY = drawSectionBarPdf(doc, { y: cursorY, width: contentWidth, text: 'TENDÊNCIA DAS SEMANAS — REALIZADO VS. META' });
    cursorY = addChartImagePdf(doc, { chart: weeklyChart, y: cursorY, maxWidth: contentWidth });
  }

  // ── Gráfico: Receita por dia da semana ──
  if (weekdayChart) {
    const imgH = contentWidth * (weekdayChart.height / weekdayChart.width);
    cursorY = ensurePdfSpace(doc, cursorY, 7 + 4 + imgH + 8);
    cursorY = drawSectionBarPdf(doc, { y: cursorY, width: contentWidth, text: 'RECEITA POR DIA DA SEMANA' });
    cursorY = addChartImagePdf(doc, { chart: weekdayChart, y: cursorY, maxWidth: contentWidth });
  }

  // ── Semanas ──
  const weekHeaders = ['Semana', 'Receita', 'Orçado', 'Fechado', 'Conversão', 'Média prof.', 'Meta', 'Atingimento', 'Status'];
  autoTable(doc, {
    startY: cursorY,
    margin,
    styles: { ...baseStyles, fontSize: 7.8 },
    head: [
      [{ content: 'SEMANAS', colSpan: weekHeaders.length, styles: { ...baseHeadStyles, fontSize: 10 } }],
      weekHeaders,
    ],
    headStyles: { fillColor: [46, 64, 84], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.8 },
    body: weeks.map((w) => [
      `Semana ${w.week_number}`,
      fmtBRL(w.revenue_amount),
      fmtBRL(w.budgets_amount),
      fmtBRL(w.closed_budgets_amount),
      pct(w.value_conversion),
      Number(w.avg_dentists || 0).toFixed(1),
      w.target_value !== null ? fmtBRL(w.target_value) : '—',
      pctOrDash(w.achievement),
      w.achievement_status ? w.achievement_status.label : '—',
    ]),
    alternateRowStyles,
    didParseCell(data) {
      if (data.section !== 'body') return;
      if (data.column.index === weekHeaders.length - 1) {
        const w = weeks[data.row.index];
        const color = SEMAFORO_RGB[statusColor(w.achievement_status)];
        if (color) { data.cell.styles.textColor = color; data.cell.styles.fontStyle = 'bold'; }
      }
    },
  });
  cursorY = doc.lastAutoTable.finalY + 8;

  // ── Contadores de produção ──
  if (counters.length) {
    const counterHeaders = ['Tipo', 'Realizado', 'Meta', 'Atingimento', 'Status'];
    autoTable(doc, {
      startY: cursorY,
      margin,
      styles: baseStyles,
      head: [
        [{ content: 'CONTADORES DE PRODUÇÃO', colSpan: counterHeaders.length, styles: { ...baseHeadStyles, fontSize: 10 } }],
        counterHeaders,
      ],
      headStyles: { fillColor: [46, 64, 84], textColor: [255, 255, 255], fontStyle: 'bold' },
      body: counters.map((c) => [
        goalTypeLabel(goalTypeCatalog, c.counter_type),
        String(c.realized),
        c.target_value !== null ? String(c.target_value) : '—',
        pctOrDash(c.achievement),
        c.status ? c.status.label : '—',
      ]),
      alternateRowStyles,
      didParseCell(data) {
        if (data.section !== 'body') return;
        if (data.column.index === counterHeaders.length - 1) {
          const c = counters[data.row.index];
          const color = SEMAFORO_RGB[statusColor(c.status)];
          if (color) { data.cell.styles.textColor = color; data.cell.styles.fontStyle = 'bold'; }
        }
      },
    });
    cursorY = doc.lastAutoTable.finalY + 8;
  }

  // ── Despesas por categoria ──
  if (monthly.expenses_by_category?.length) {
    autoTable(doc, {
      startY: cursorY,
      margin,
      styles: baseStyles,
      head: [
        [{ content: 'DESPESAS POR CATEGORIA', colSpan: 3, styles: { ...baseHeadStyles, fontSize: 10 } }],
        ['Categoria', 'Valor', 'Descrição'],
      ],
      headStyles: { fillColor: [46, 64, 84], textColor: [255, 255, 255], fontStyle: 'bold' },
      body: monthly.expenses_by_category.map((e) => [e.category, fmtBRL(e.amount), e.description || '—']),
      alternateRowStyles,
    });
    cursorY = doc.lastAutoTable.finalY + 8;
  }

  // ── Alertas ──
  autoTable(doc, {
    startY: cursorY,
    margin,
    styles: baseStyles,
    head: [
      [{ content: 'ALERTAS', colSpan: 3, styles: { ...baseHeadStyles, fontSize: 10 } }],
      ['Severidade', 'Regra', 'Mensagem'],
    ],
    headStyles: { fillColor: [46, 64, 84], textColor: [255, 255, 255], fontStyle: 'bold' },
    body: alerts.length
      ? alerts.map((a) => [(SEVERITY_LABELS[a.severity] || a.severity).toUpperCase(), a.rule, a.message])
      : [['—', '—', 'Nenhum alerta no período.']],
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 38 } },
    alternateRowStyles,
    didParseCell(data) {
      if (data.section !== 'body' || !alerts.length) return;
      const a = alerts[data.row.index];
      const color = SEVERITY_RGB[a.severity];
      if (color && data.column.index === 0) {
        data.cell.styles.textColor = color;
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // ── Rodapé com paginação em todas as páginas ──
  drawPdfFooter(doc, `${COMPANY_BRAND} · Resultados da Unidade · ${company.name}`);

  doc.save(`resultados-${company.name}-${period.year}-${String(period.month).padStart(2, '0')}.pdf`);
}

// ─────────────────────────────────────────────────────────────────────────
// Excel (exceljs) — logo embutido, cabeçalhos coloridos, largura automática
// ─────────────────────────────────────────────────────────────────────────

/**
 * `goalTypeCatalog` é o catálogo de tipos de meta/contador da empresa (ver
 * fetchGoalTypes/goalTypeLabel em clinicalUtils.js) — necessário pra
 * traduzir `counter_type` em label; a função não tem acesso a company_id
 * pra buscar sozinha, então o chamador (componente) já deve ter carregado
 * e passar aqui.
 */
export async function exportMonthlyXlsx(dashboard, goalTypeCatalog = [], charts = {}) {
  const { company, period, monthly, weeks, counters } = dashboard;
  const monthLabel = `${MONTH_NAMES[period.month - 1]}/${period.year}`;

  // Carga dinâmica para não inflar o bundle inicial (mesmo padrão de
  // `components/exportUtils.js` para jspdf/jspdf-autotable).
  const [{ default: ExcelJS }, weeklyChart, weekdayChart] = await Promise.all([
    import('exceljs'),
    captureChartImage(charts.weeklyTrendEl),
    captureChartImage(charts.weekdayEl),
  ]);
  const wb = new ExcelJS.Workbook();
  wb.creator = COMPANY_BRAND;
  wb.created = new Date();
  const ws = wb.addWorksheet('Resultados');

  const widths = []; // maxLen por coluna (1-based, índice 0 = coluna A)
  const trackWidth = (col, text) => {
    const len = String(text ?? '').length;
    widths[col - 1] = Math.max(widths[col - 1] || 0, len);
  };

  let r = 1;
  const logoB64 = await getLogoBase64();
  if (logoB64) {
    const imgId = wb.addImage({ base64: logoB64, extension: 'jpeg' });
    ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: 110, height: 62 } });
    r = 6; // reserva espaço pro logo antes do título
  }

  const setCell = (row, col, value, style = {}) => {
    const cell = ws.getCell(row, col);
    cell.value = value;
    Object.assign(cell, style);
    // Aproximação de largura: para células com numFmt (moeda/percentual) o
    // texto formatado costuma ser um pouco maior que o valor cru — soma
    // uma folga fixa em vez de tentar replicar a formatação do Excel aqui.
    const approxLen = String(value ?? '').length + (style.numFmt ? 4 : 0);
    trackWidth(col, ' '.repeat(approxLen));
    return cell;
  };

  const sectionHeader = (text, span) => {
    r = xlsxSectionHeader(ws, r, text, span);
  };

  const columnHeaderRow = (headers) => {
    headers.forEach((h, i) => {
      setCell(r, i + 1, h, {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NAVY_LIGHT_ARGB } },
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
      });
    });
    r += 1;
  };

  const blank = () => { r += 1; };

  // ── Título ──
  setCell(r, 1, `GESTÃO DE RESULTADOS — ${company.name}`, { font: { bold: true, size: 13, color: { argb: BRAND_NAVY_ARGB } } });
  r += 1;
  setCell(r, 1, `Período: ${monthLabel}`, { font: { size: 9, color: { argb: 'FF888888' } } });
  r += 1;
  setCell(r, 1, genLabel(), { font: { size: 8, italic: true, color: { argb: 'FF9AA3AF' } } });
  blank();

  // ── Resumo do mês — cards de KPI ──
  sectionHeader('RESUMO DO MÊS', 9);
  const overallColor = SEMAFORO_ARGB[monthly.status.overall];
  r = xlsxKpiCards(ws, r, [
    { label: 'Receita total', value: fmtBRL(monthly.total_revenue) },
    { label: 'Despesas', value: fmtBRL(monthly.total_expenses) },
    { label: 'Lucro líquido', value: fmtBRL(monthly.net_profit) },
    { label: 'Margem líquida', value: pct(monthly.net_margin) },
    { label: 'Conversão em valor', value: pct(monthly.value_conversion) },
    { label: 'Ticket médio', value: fmtBRL(monthly.average_ticket) },
    { label: 'Ticket médio dos orçamentos', value: fmtBRL(monthly.avg_budget_ticket) },
    { label: 'Ticket médio dos orç. fechados', value: fmtBRL(monthly.avg_closed_budget_ticket) },
    { label: 'Atingimento da meta', value: pctOrDash(monthly.achievement) },
    { label: 'Semáforo geral', value: (monthly.status.overall || '—').toUpperCase(), colorArgb: overallColor },
  ], { totalCols: 9, perRow: 3 });
  blank();

  // ── Gráficos embutidos ──
  if (weeklyChart) {
    sectionHeader('TENDÊNCIA DAS SEMANAS — REALIZADO VS. META', 9);
    r = addChartImageXlsx(wb, ws, { chart: weeklyChart, row: r, widthPx: 620 });
    blank();
  }
  if (weekdayChart) {
    sectionHeader('RECEITA POR DIA DA SEMANA', 9);
    r = addChartImageXlsx(wb, ws, { chart: weekdayChart, row: r, widthPx: 620 });
    blank();
  }

  // ── Semanas ──
  const weekHeaders = ['Semana', 'Receita', 'Orçado', 'Fechado', 'Conversão', 'Média profissionais', 'Meta', 'Atingimento', 'Status'];
  sectionHeader('SEMANAS', weekHeaders.length);
  const weeksHeaderRow = r;
  columnHeaderRow(weekHeaders);
  for (const w of weeks) {
    setCell(r, 1, `Semana ${w.week_number}`);
    setCell(r, 2, brl(w.revenue_amount), { numFmt: MONEY_FMT });
    setCell(r, 3, brl(w.budgets_amount), { numFmt: MONEY_FMT });
    setCell(r, 4, brl(w.closed_budgets_amount), { numFmt: MONEY_FMT });
    setCell(r, 5, Number(w.value_conversion) || 0, { numFmt: PCT_FMT });
    setCell(r, 6, Number(w.avg_dentists || 0), { numFmt: '0.0' });
    setCell(r, 7, w.target_value !== null ? brl(w.target_value) : '—', w.target_value !== null ? { numFmt: MONEY_FMT } : {});
    setCell(r, 8, w.achievement !== null ? Number(w.achievement) : '—', w.achievement !== null ? { numFmt: PCT_FMT } : {});
    const statusArgb = SEMAFORO_ARGB[statusColor(w.achievement_status)];
    setCell(r, 9, w.achievement_status ? w.achievement_status.label : '—', statusArgb ? { font: { bold: true, color: { argb: statusArgb } } } : {});
    r += 1;
  }
  const weeksLastRow = r - 1;
  if (weeksLastRow >= weeksHeaderRow) {
    ws.autoFilter = { from: { row: weeksHeaderRow, column: 1 }, to: { row: weeksLastRow, column: weekHeaders.length } };
  }
  ws.views = [{ state: 'frozen', ySplit: weeksHeaderRow }];
  blank();

  // ── Contadores de produção ──
  if (counters.length) {
    const counterHeaders = ['Tipo', 'Realizado', 'Meta', 'Atingimento', 'Status'];
    sectionHeader('CONTADORES DE PRODUÇÃO', counterHeaders.length);
    columnHeaderRow(counterHeaders);
    for (const c of counters) {
      setCell(r, 1, goalTypeLabel(goalTypeCatalog, c.counter_type));
      setCell(r, 2, c.realized);
      setCell(r, 3, c.target_value !== null ? c.target_value : '—');
      setCell(r, 4, c.achievement !== null ? Number(c.achievement) : '—', c.achievement !== null ? { numFmt: PCT_FMT } : {});
      const statusArgb = SEMAFORO_ARGB[statusColor(c.status)];
      setCell(r, 5, c.status ? c.status.label : '—', statusArgb ? { font: { bold: true, color: { argb: statusArgb } } } : {});
      r += 1;
    }
    blank();
  }

  // ── Despesas por categoria ──
  if (monthly.expenses_by_category?.length) {
    sectionHeader('DESPESAS POR CATEGORIA', 3);
    columnHeaderRow(['Categoria', 'Valor', 'Descrição']);
    for (const e of monthly.expenses_by_category) {
      setCell(r, 1, e.category);
      setCell(r, 2, brl(e.amount), { numFmt: MONEY_FMT });
      setCell(r, 3, e.description || '—');
      r += 1;
    }
  }

  // ── Largura automática das colunas ──
  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = Math.min(Math.max(w + 2, 12), 40);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `resultados-${company.name}-${period.year}-${String(period.month).padStart(2, '0')}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
